import React from 'react';
import { Plus, X } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Checkbox } from '../../components/ui/checkbox';
import { Combobox } from '../../components/ui/Combobox';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { SelectField } from '../../components/ui/SelectField';
import { Textarea } from '../../components/ui/textarea';
import { AF_COUNTRY_VALUES } from '../../lib/leadRelay/anytimefitness';
import { BLOCKED_INTERESTS } from '../../lib/leadRelay/blocklist';
import { LEAD_LOCALES } from '../../lib/leadRelay/locale';
import { PROVIDER_CAN_SEND, providerNotWiredMessage, providerVocabulary } from '../../lib/leadRelay/providerVocabulary';
import {
  describeModelMapRows,
  type ModelMapStandard,
  removeModelMapRow,
  resetModelMapRow,
  setModelMapRow,
} from '../../lib/leadRelay/modelMapStandard';
import {
  type AnytimeFitnessFormFields,
  ROUTE_FIELD_LABELS,
  type RouteFormCommon,
  type RouteFormField,
  type RouteFormState,
  SAMPLE_PREVIEW_LEAD,
  toggleListItem,
  type VirtualYardFormFields,
} from '../../lib/leadRelay/routeForm';
import { LEAD_ROUTE_SETUPS, type LeadRouteSetup } from '../../lib/leadRelay/routeSetups';
import { formUrlTokenTail, renderMessage, SENDABLE_INTEREST_OPTIONS } from '../../lib/leadRelay/virtualyard';

// The body of the lead-route editor (docs/lead-relay.md §10.1, §25) —
// presentational, so it is server-rendered in its tests rather than mounted in
// jsdom (the suite sits close to the jsdom timeout cliff).
//
// ⚠️⚠️ A NEW ROUTE SHOWS ONLY THE SETUP PICKER UNTIL A SETUP IS CHOSEN. A form
// that reshapes itself after the fact is worse than one that waits: fields
// typed into one provider's form would silently mean nothing to the other. So
// `form` is null until the choice, and nothing else renders.
//
// ⚠️ Once chosen, the form shows ONLY that provider's fields. No greyed-out
// VirtualYard fields on an Anytime Fitness route, and the provider itself is
// displayed, never editable.

/** Patches the setters accept. ⛔ None can carry `provider` (or an AF route's country). */
export type CommonPatch = Partial<Omit<RouteFormCommon, never>>;
export type VirtualYardPatch = Partial<Omit<VirtualYardFormFields, 'provider' | 'modelMapStandardId'>>;
export type AnytimeFitnessPatch = Partial<Omit<AnytimeFitnessFormFields, 'provider' | 'country'>>;

export interface LeadRouteEditorBodyProps {
  isNew: boolean;
  /** null = a new route whose setup has not been chosen yet. */
  form: RouteFormState | null;
  onChooseSetup: (setup: LeadRouteSetup) => void;
  changed: ReadonlySet<RouteFormField>;
  problemsFor: (field: string) => string[];
  idProblem: string | null;
  accountOptions: Array<{ value: string; label: string }>;
  accountClash: string | null;
  inFlight: number;
  onCommon: (patch: CommonPatch) => void;
  onVirtualYard: (patch: VirtualYardPatch) => void;
  onAnytimeFitness: (patch: AnytimeFitnessPatch) => void;
  /** The shared model map a VirtualYard route inherits from; null when none, or not loaded yet. */
  modelMapStandard: ModelMapStandard | null;
}

function Section({ title, description, children }: { title: string; description?: string; children: React.ReactNode }) {
  return (
    <Card>
      <CardHeader>
        <CardTitle>{title}</CardTitle>
        {description && <p className="text-sm text-muted-foreground">{description}</p>}
      </CardHeader>
      <CardContent className="space-y-4">{children}</CardContent>
    </Card>
  );
}

function FieldLabel({
  htmlFor,
  field,
  changed,
  children,
}: {
  htmlFor?: string;
  field: RouteFormField;
  changed: ReadonlySet<RouteFormField>;
  children?: React.ReactNode;
}) {
  return (
    <Label htmlFor={htmlFor} className="flex items-center gap-1.5">
      {children ?? ROUTE_FIELD_LABELS[field]}
      {changed.has(field) && (
        <span className="h-2 w-2 rounded-full bg-amber-500" title="Changed — not saved yet" aria-label="changed" />
      )}
    </Label>
  );
}

function Problems({ messages }: { messages: string[] }) {
  if (messages.length === 0) return null;
  return (
    <ul className="space-y-0.5 text-sm text-red-700">
      {messages.map((m) => (
        <li key={m}>{m}</li>
      ))}
    </ul>
  );
}

/** The Setup select — the only thing a new route shows until a setup is chosen. */
export function RouteSetupPicker({ onChoose }: { onChoose: (setup: LeadRouteSetup) => void }) {
  return (
    <Section
      title="Setup"
      description="Choose the setup this route uses. It fixes the provider — how leads are sent — and loads that setup's fields. It cannot be changed after the route is created."
    >
      <div className="max-w-sm" data-testid="route-setup-picker">
        <SelectField
          value=""
          onValueChange={(id) => {
            const setup = LEAD_ROUTE_SETUPS.find((s) => s.id === id);
            if (setup) onChoose(setup);
          }}
          options={LEAD_ROUTE_SETUPS.map((s) => ({ value: s.id, label: s.label }))}
          placeholder="Choose a setup"
          aria-label="Setup"
        />
      </div>
      <ul className="space-y-1 text-sm text-muted-foreground">
        {LEAD_ROUTE_SETUPS.map((s) => (
          <li key={s.id}>
            <span className="font-medium text-foreground">{s.label}</span> — {s.description}
          </li>
        ))}
      </ul>
    </Section>
  );
}

export function LeadRouteEditorBody(props: LeadRouteEditorBodyProps) {
  const { form } = props;
  // ⛔ THE GATE: nothing but the picker until a setup is chosen.
  if (form === null) return <RouteSetupPicker onChoose={props.onChooseSetup} />;
  return (
    <div className="space-y-6" data-testid="route-editor-form">
      <RouteSection {...props} form={form} />
      {form.provider === 'virtualyard' ? (
        <VirtualYardSections {...props} form={form} />
      ) : (
        <AnytimeFitnessSections {...props} form={form} />
      )}
      <MetaFormsSection {...props} form={form} />
      <Section title="Alerts" description="Failures alert in Slack. Leave blank for the relay's default channel.">
        <div className="max-w-sm">
          <FieldLabel htmlFor="route-alert" field="alertChannel" changed={props.changed}>
            Slack channel id
          </FieldLabel>
          <Input
            id="route-alert"
            className="mt-1"
            value={form.alertChannel}
            placeholder="C0123456789"
            onChange={(e) => props.onCommon({ alertChannel: e.target.value })}
          />
        </div>
      </Section>
    </div>
  );
}

type WithForm<F> = Omit<LeadRouteEditorBodyProps, 'form'> & { form: F };

function RouteSection({
  isNew,
  form,
  changed,
  problemsFor,
  idProblem,
  accountOptions,
  accountClash,
  inFlight,
  onCommon,
  onVirtualYard,
  onAnytimeFitness,
}: WithForm<RouteFormState>) {
  const vocab = providerVocabulary(form.provider);
  const canSend = PROVIDER_CAN_SEND[form.provider];
  return (
    <Section
      title="Route"
      description={`The ${vocab.destination.toLowerCase()} this route sends leads to. The ${vocab.destinationId.toLowerCase()} alone decides where they go.`}
    >
      {/* ⛔ The provider is DISPLAYED, never editable — no control may change it. */}
      <div className="text-sm" data-testid="route-provider">
        <span className="text-muted-foreground">Provider: </span>
        <span className="font-medium">{vocab.system}</span>
        <p className="mt-0.5 text-xs text-muted-foreground">
          Fixed when the route was created and never editable — it decides how every other field is read. A different
          provider is a different route.
        </p>
      </div>
      {isNew && (
        <div>
          <Label htmlFor="route-id">Route id</Label>
          <Input
            id="route-id"
            className="mt-1"
            value={form.id}
            placeholder={form.provider === 'virtualyard' ? 'byd-liverpool' : 'af-fortitude-valley'}
            onChange={(e) => onCommon({ id: e.target.value })}
          />
          {form.id !== '' && idProblem && <p className="mt-1 text-sm text-red-700">{idProblem}</p>}
        </div>
      )}
      <div className="grid gap-4 sm:grid-cols-2">
        <div>
          <FieldLabel htmlFor="route-name" field="name" changed={changed} />
          <Input id="route-name" className="mt-1" value={form.name} onChange={(e) => onCommon({ name: e.target.value })} />
          <Problems messages={problemsFor('name')} />
        </div>
        <div>
          <FieldLabel field="accountId" changed={changed} />
          <div className="mt-1">
            <Combobox
              value={form.accountId}
              onValueChange={(v) => onCommon({ accountId: v })}
              options={accountOptions}
              placeholder={`Choose the ${vocab.destination.toLowerCase()}'s account`}
              searchPlaceholder="Search accounts"
              aria-label="Account"
            />
          </div>
          <Problems messages={problemsFor('accountId')} />
        </div>
        {form.provider === 'virtualyard' ? (
          <div>
            <FieldLabel htmlFor="route-lid" field="lid" changed={changed} />
            <Input
              id="route-lid"
              className="mt-1 font-mono"
              value={form.lid}
              placeholder={vocab.destinationIdExample}
              onChange={(e) => onVirtualYard({ lid: e.target.value })}
            />
            <p className="mt-1 text-xs text-muted-foreground">
              The store's value from the dealer form's store dropdown, colon included. A wrong value sends every lead to
              another dealership with nothing in the log to show it.
            </p>
            <Problems messages={problemsFor('lid')} />
          </div>
        ) : (
          <div>
            <FieldLabel htmlFor="route-club-id" field="clubId" changed={changed} />
            <Input
              id="route-club-id"
              className="mt-1 font-mono"
              value={form.clubId}
              placeholder={AF_COUNTRY_VALUES[form.country].clubIdExample}
              onChange={(e) => onAnytimeFitness({ clubId: e.target.value })}
            />
            <p className="mt-1 text-xs text-muted-foreground">
              The Anytime Fitness club id. It alone decides which club receives a lead — a wrong one is accepted by
              Anytime Fitness and nothing here can detect it. The first live lead on every new club is confirmed with
              that club, by a person.
            </p>
            <Problems messages={problemsFor('clubId')} />
          </div>
        )}
      </div>
      <div className="flex flex-wrap gap-6">
        <label className="flex items-center gap-2 text-sm">
          <Checkbox
            checked={form.active}
            disabled={!canSend && !form.active}
            onCheckedChange={(v) => onCommon({ active: v === true })}
          />
          <FieldLabel field="active" changed={changed}>
            Active — receives leads
          </FieldLabel>
        </label>
        <label className="flex items-center gap-2 text-sm">
          <Checkbox checked={form.testMode} onCheckedChange={(v) => onCommon({ testMode: v === true })} />
          <FieldLabel field="testMode" changed={changed}>
            Test mode — record leads, never submit
          </FieldLabel>
        </label>
      </div>
      {!canSend && (
        <p className="text-sm text-amber-700" data-testid="provider-not-wired">
          {providerNotWiredMessage(form.provider)}
        </p>
      )}
      <Problems messages={problemsFor('active')} />
      {accountClash && (
        <p className="text-sm text-red-700" role="alert" data-testid="second-active-route-problem">
          {accountClash}
        </p>
      )}
      {form.testMode && inFlight > 0 && (
        <p className="text-sm text-amber-700">
          {inFlight} lead{inFlight === 1 ? ' is' : 's are'} already queued for this route and will still be submitted —
          test mode stops new leads only.
        </p>
      )}
    </Section>
  );
}

function MetaFormsSection({ form, changed, problemsFor, onCommon }: WithForm<RouteFormState>) {
  const [newFormName, setNewFormName] = React.useState('');
  return (
    <Section
      title="Meta lead forms"
      description="The Meta lead form names for this route, exactly as named in Meta. They do NOT route leads — the account does (account_id). They are only the SUGGESTION shown when a lead arrives unassigned."
    >
      <div className="space-y-2">
        <FieldLabel field="metaFormNames" changed={changed} />
        {form.metaFormNames.length === 0 && (
          <p className="text-sm text-muted-foreground">
            No forms yet — leads still arrive by account_id; an unassigned one just gets no suggestion.
          </p>
        )}
        {form.metaFormNames.map((name) => (
          <div key={name} className="flex items-center gap-2">
            <span className="flex-1 rounded-md border px-3 py-1.5 text-sm">{name}</span>
            <Button
              variant="ghost"
              size="icon"
              aria-label={`Remove ${name}`}
              onClick={() => onCommon({ metaFormNames: form.metaFormNames.filter((n) => n !== name) })}
            >
              <X className="h-4 w-4" />
            </Button>
          </div>
        ))}
        <div className="flex gap-2">
          <Input
            value={newFormName}
            placeholder={form.provider === 'virtualyard' ? 'BYD Liverpool — Spring' : 'AF Fortitude Valley — Join'}
            onChange={(e) => setNewFormName(e.target.value)}
            aria-label="New Meta form name"
          />
          <Button
            variant="outline"
            disabled={newFormName.trim() === ''}
            onClick={() => {
              onCommon({ metaFormNames: toggleListItem(form.metaFormNames, newFormName.trim(), true) });
              setNewFormName('');
            }}
          >
            <Plus className="mr-1 h-4 w-4" />
            Add
          </Button>
        </div>
        <Problems messages={problemsFor('metaFormNames')} />
      </div>
      <div className="rounded-md bg-muted/50 p-3 text-sm">
        <p className="font-medium">Building the Meta form</p>
        {form.provider === 'virtualyard' ? (
          <p className="mt-1 text-muted-foreground">
            Ask for first name, last name, email, phone and postcode (VirtualYard requires the postcode), plus an explicit
            marketing opt-in question. Author the model question's answers so they match the model map below.
          </p>
        ) : (
          <p className="mt-1 text-muted-foreground">
            Ask for first name, last name, email and mobile (Anytime Fitness requires the mobile); the postcode is
            optional. There is no model or interest question — every lead is a membership enquiry.
          </p>
        )}
      </div>
    </Section>
  );
}

function VirtualYardSections({
  form,
  changed,
  problemsFor,
  onVirtualYard,
  modelMapStandard,
}: WithForm<RouteFormCommon & VirtualYardFormFields>) {
  const tokenTail = formUrlTokenTail(form.formUrl);
  const preview = renderMessage(form.messageTemplate, SAMPLE_PREVIEW_LEAD);
  return (
    <>
      <Section
        title="Dealer form"
        description="The dealer's Enquire Now form. Every store of the group shares it; the store id above picks the store."
      >
        <div>
          <FieldLabel htmlFor="route-form-url" field="formUrl" changed={changed} />
          <Input
            id="route-form-url"
            className="mt-1 font-mono text-xs"
            value={form.formUrl}
            onChange={(e) => onVirtualYard({ formUrl: e.target.value })}
          />
          <p className="mt-1 text-xs text-muted-foreground">
            It carries the dealer's form token — the key to submitting as that dealer. It is never logged; the audit trail
            records only the token's last six characters{tokenTail ? ` (…${tokenTail})` : ''}.
          </p>
          <Problems messages={problemsFor('formUrl')} />
        </div>
      </Section>

      <Section
        title="Interests"
        description={`Which "Interested in" options this route may send. ${BLOCKED_INTERESTS.join(' and ')} can never be sent: they pass the customer's details to EV Direct Finance and OLY, which a Meta lead never agreed to.`}
      >
        <div>
          <FieldLabel field="allowedInterests" changed={changed} />
          <div className="mt-2 grid gap-1 sm:grid-cols-2">
            {SENDABLE_INTEREST_OPTIONS.map((option) => (
              <label key={option} className="flex items-center gap-2 text-sm">
                <Checkbox
                  checked={form.allowedInterests.includes(option)}
                  onCheckedChange={(v) =>
                    onVirtualYard({ allowedInterests: toggleListItem(form.allowedInterests, option, v === true) })
                  }
                />
                {option}
              </label>
            ))}
            {BLOCKED_INTERESTS.map((option) => (
              <label key={option} className="flex items-center gap-2 text-sm text-muted-foreground" title="Blocked in code">
                <Checkbox checked={false} disabled />
                {option} (blocked)
              </label>
            ))}
          </div>
          <Problems messages={problemsFor('allowedInterests')} />
        </div>
        <div className="max-w-xs">
          <FieldLabel field="defaultInterest" changed={changed} />
          <div className="mt-1">
            <SelectField
              value={form.defaultInterest}
              onValueChange={(v) => onVirtualYard({ defaultInterest: v })}
              options={form.allowedInterests.map((o) => ({ value: o, label: o }))}
              aria-label="Default interest"
            />
          </div>
          <p className="mt-1 text-xs text-muted-foreground">Sent when the lead's model answer does not match an option.</p>
          <Problems messages={problemsFor('defaultInterest')} />
        </div>
      </Section>

      <ModelMapSection
        form={form}
        changed={changed}
        problemsFor={problemsFor}
        onVirtualYard={onVirtualYard}
        standard={modelMapStandard}
      />

      <Section
        title="Message"
        description="What the dealer reads in VirtualYard's Message field. The first line must stay the Facebook Lead marker. Placeholders: {campaign_name} {form_name} {model} {extra}."
      >
        <div className="grid gap-4 lg:grid-cols-2">
          <div>
            <FieldLabel htmlFor="route-template" field="messageTemplate" changed={changed} />
            <Textarea
              id="route-template"
              className="mt-1 font-mono text-sm"
              rows={7}
              value={form.messageTemplate}
              onChange={(e) => onVirtualYard({ messageTemplate: e.target.value })}
            />
            <Problems messages={problemsFor('messageTemplate')} />
          </div>
          <div>
            <Label>Preview (a sample lead)</Label>
            {/* Rendered by the relay's own renderMessage — the preview IS what will be sent. */}
            <pre
              className="mt-1 min-h-[9rem] whitespace-pre-wrap rounded-md border bg-muted/40 p-3 font-sans text-sm"
              data-testid="message-preview"
            >
              {preview}
            </pre>
          </div>
        </div>
      </Section>
    </>
  );
}

function AnytimeFitnessSections({ form }: WithForm<RouteFormCommon & AnytimeFitnessFormFields>) {
  // ⚠️ Per-country DATA, read from the route's country — never stored on the
  // route and never written out here, so NZ and the UK are table entries.
  const values = AF_COUNTRY_VALUES[form.country];
  const rules = LEAD_LOCALES[form.country];
  return (
    <Section
      title="Country"
      description="Fixed by the setup (Australia or New Zealand) and not changeable on an existing route. These values are sent with every lead and come from the country — they are not stored on the route."
    >
      <dl className="grid gap-x-4 gap-y-1 text-sm sm:grid-cols-[10rem_1fr]" data-testid="af-country-values">
        <dt className="text-muted-foreground">Country</dt>
        <dd>{rules.countryName}</dd>
        <dt className="text-muted-foreground">Locale</dt>
        <dd className="font-mono">{values.locale}</dd>
        <dt className="text-muted-foreground">Website</dt>
        <dd className="font-mono">{values.websiteUrl}</dd>
        <dt className="text-muted-foreground">Phone</dt>
        <dd>
          Sent as <span className="font-mono">+{rules.callingCode}</span> — a lead's phone is required, and one that cannot
          be read is refused rather than guessed.
        </dd>
        <dt className="text-muted-foreground">Postcode</dt>
        <dd>{rules.postcodeDescription}, optional — an invalid one is left out of the lead, never sent.</dd>
      </dl>
    </Section>
  );
}

/**
 * ⭐ The model map, as the standard plus this route's DIFF (§25).
 *
 * ⚠️⚠️ AN OVERRIDE YOU CANNOT SEE IS THE BUG. Every row says where it comes
 * from — the shared standard, an override of it (with what the standard would
 * have sent), or a row only this route has — and a standard row this route
 * removes is listed rather than silently missing. Reset per row, and reset all.
 * Only the overrides are saved; the standard's own rows never are, so a model
 * added to the standard later reaches this route too.
 */
function ModelMapSection({
  form,
  changed,
  problemsFor,
  onVirtualYard,
  standard,
}: {
  form: RouteFormCommon & VirtualYardFormFields;
  changed: ReadonlySet<RouteFormField>;
  problemsFor: (field: string) => string[];
  onVirtualYard: (patch: VirtualYardPatch) => void;
  standard: ModelMapStandard | null;
}) {
  const [newAnswer, setNewAnswer] = React.useState('');
  const [newValue, setNewValue] = React.useState(form.defaultInterest);
  const base = standard?.map ?? null;
  const view = describeModelMapRows(base, form.modelMapOverrides);
  const overrides = form.modelMapOverrides;
  const label = standard?.label ?? 'shared standard';
  const set = (next: typeof overrides) => onVirtualYard({ modelMapOverrides: next });
  const options = form.allowedInterests.map((o) => ({ value: o, label: o }));
  const overrideTotal = view.rows.filter((r) => r.origin !== 'standard').length + view.removed.length;
  const standardUnavailable = form.modelMapStandardId !== null && standard === null;
  return (
    <Section
      title="Model map"
      description={
        form.modelMapStandardId
          ? `Meta answer → VirtualYard option. Rows come from the ${label}, shared by every route that uses it; a row changed here is an override of it. Only the overrides are saved on this route, so a model added to the standard reaches this route too.`
          : 'Meta answer → VirtualYard option. This route uses no shared standard: every row below is its own.'
      }
    >
      <FieldLabel field="modelMapOverrides" changed={changed} />
      {/* ⚠️ A verdict ("matches exactly", "N rows differ") is a comparison with
          the standard. With the standard unavailable it cannot be made, so the
          screen says THAT — one line — and never computes a verdict from the
          overrides alone. */}
      {standardUnavailable ? (
        <p className="text-sm text-red-700" data-testid="model-map-standard-missing">
          The shared model map "{form.modelMapStandardId}" could not be loaded, so this route cannot be compared with it
          and the rows it provides are not shown.
        </p>
      ) : (
        <p className="text-sm text-muted-foreground" data-testid="model-map-override-summary">
          {overrideTotal === 0
            ? form.modelMapStandardId
              ? `Matches the ${label} exactly — no overrides.`
              : 'No rows yet.'
            : `${overrideTotal} row${overrideTotal === 1 ? '' : 's'} differ${overrideTotal === 1 ? 's' : ''} from the ${label}.`}
        </p>
      )}
      <div className="space-y-2" data-testid="model-map-rows">
        {view.rows.map((row) => (
          <div key={row.answer} className="flex flex-wrap items-center gap-2" data-origin={row.origin}>
            <span className="w-40 shrink-0 font-mono text-sm">{row.answer}</span>
            <span className="text-muted-foreground">→</span>
            <div className="w-56 shrink-0">
              <SelectField
                value={row.value}
                onValueChange={(v) => set(setModelMapRow(base, overrides, row.answer, v))}
                options={options}
                aria-label={`VirtualYard option for ${row.answer}`}
              />
            </div>
            {row.origin === 'standard' && (
              <span className="text-xs text-muted-foreground" data-testid="model-map-origin">
                From the {label}
              </span>
            )}
            {row.origin === 'overridden' && (
              <span className="text-xs font-medium text-amber-700" data-testid="model-map-origin">
                Overridden — the {label} sends "{row.standardValue}"
              </span>
            )}
            {row.origin === 'added' && (
              <span className="text-xs font-medium text-amber-700" data-testid="model-map-origin">
                {standardUnavailable ? 'Set on this route' : form.modelMapStandardId ? 'Only on this route' : 'This route'}
              </span>
            )}
            {row.origin === 'overridden' && (
              <Button variant="ghost" size="sm" onClick={() => set(resetModelMapRow(overrides, row.answer))}>
                Reset to standard
              </Button>
            )}
            <Button
              variant="ghost"
              size="icon"
              aria-label={`Remove ${row.answer}`}
              onClick={() => set(removeModelMapRow(base, overrides, row.answer))}
            >
              <X className="h-4 w-4" />
            </Button>
          </div>
        ))}
        {view.removed.map((row) => (
          <div key={row.answer} className="flex flex-wrap items-center gap-2 text-sm" data-origin="removed">
            <span className="w-40 shrink-0 font-mono text-muted-foreground line-through">{row.answer}</span>
            <span className="text-xs font-medium text-amber-700" data-testid="model-map-origin">
              Removed on this route — the {label} sends "{row.standardValue}"; here it falls back to the default
            </span>
            <Button variant="ghost" size="sm" onClick={() => set(resetModelMapRow(overrides, row.answer))}>
              Reset to standard
            </Button>
          </div>
        ))}
      </div>
      <div className="flex flex-wrap items-center gap-2">
        <Input
          className="w-40"
          value={newAnswer}
          placeholder="Meta answer"
          aria-label="New Meta answer"
          onChange={(e) => setNewAnswer(e.target.value)}
        />
        <span className="text-muted-foreground">→</span>
        <div className="w-56 shrink-0">
          <SelectField value={newValue} onValueChange={setNewValue} options={options} aria-label="New VirtualYard option" />
        </div>
        <Button
          variant="outline"
          disabled={newAnswer.trim() === ''}
          onClick={() => {
            set(setModelMapRow(base, overrides, newAnswer, newValue));
            setNewAnswer('');
          }}
        >
          <Plus className="mr-1 h-4 w-4" />
          Add mapping
        </Button>
        {form.modelMapStandardId && overrideTotal > 0 && (
          <Button variant="ghost" onClick={() => set({})}>
            Reset all to the {label}
          </Button>
        )}
      </div>
      <Problems messages={problemsFor('modelMap')} />
    </Section>
  );
}
